// Copyright 2022 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import { useEffect } from "react";
import browser from "webextension-polyfill";
import { getEnvInfo, getPortalConfig } from "../../../../configurations";
import {
    GetCookieSynchronizationConfigurationRequest,
    GetCookieSynchronizationConfigurationResponse,
} from "@amzn/ermine-ssr-sdk/clients/ermineserversiderenderingservice";
import { AWSError } from "aws-sdk";
import log, { METRIC_NAME } from "../../../../logging";
import { useGetErmineSsrClient } from "../../../../hooks/useGetErmineSsrClient";

interface Props {
    onCookieSyncConfigFetch: (
        allowlistFilter: browser.Cookies.GetAllDetailsType[],
        blocklistFilter: browser.Cookies.GetAllDetailsType[],
        metricName: METRIC_NAME
    ) => void;
    onCookieSyncConfigFetchError: (
        err: AWSError,
        metricName: METRIC_NAME
    ) => void;
}

const OidcCookieSyncConfigFetcher = (props: Props): JSX.Element | null => {
    const { onCookieSyncConfigFetch, onCookieSyncConfigFetchError } = props;
    const portalConfig = getPortalConfig();
    const portalArn = portalConfig.portalArn;
    const { region } = getEnvInfo();
    const endpoint = `${window.location.origin}`;
    const { ermineService, isServiceReady } = useGetErmineSsrClient();

    useEffect(() => {
        if (!isServiceReady) {
            return;
        }

        ermineService.makeUnauthenticatedRequest(
            "getCookieSynchronizationConfiguration",
            {
                portalArn,
            } as GetCookieSynchronizationConfigurationRequest,
            (
                err: AWSError,
                data: GetCookieSynchronizationConfigurationResponse
            ) => {
                if (err) {
                    log.error(
                        `OidcCookieSyncConfigFetcher ${err.statusCode} | ${err.code}: ${err.message}`
                    );
                    onCookieSyncConfigFetchError(
                        err,
                        METRIC_NAME.OIDC_GET_COOKIE_SYNC_CONFIG
                    );
                } else {
                    const { cookieSynchronizationConfiguration } = data;
                    onCookieSyncConfigFetch(
                        cookieSynchronizationConfiguration?.allowlist,
                        cookieSynchronizationConfiguration?.blocklist,
                        METRIC_NAME.OIDC_GET_COOKIE_SYNC_CONFIG
                    );
                }
            }
        );
    }, [
        endpoint,
        onCookieSyncConfigFetch,
        onCookieSyncConfigFetchError,
        portalArn,
        region,
        isServiceReady,
    ]);

    return null;
};

export default OidcCookieSyncConfigFetcher;
